import React, { useCallback, useState } from "react";
import { PlatformData } from "../../../services/Platform";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { capitalize } from "../../../utils/Strings";

interface UsageFilterBarProps {
  platforms: PlatformData[];
  filters: {
    platformId?: string;
    startDate?: string;
    endDate?: string;
    sortBy: string;
    sortOrder: "asc" | "desc";
  };
  onFilterChange: (filters: Partial<UsageFilterBarProps["filters"]>) => void;
}

const UsageFilterBar: React.FC<UsageFilterBarProps> = ({
  platforms,
  filters,
  onFilterChange,
}) => {
  // Convert string dates to dayjs objects for the DatePicker
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(
    filters.startDate ? dayjs(filters.startDate) : null
  );
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(
    filters.endDate ? dayjs(filters.endDate) : null
  );

  const handlePlatformChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value || undefined; // Convert empty string to undefined
      onFilterChange({ platformId: value });
    },
    [onFilterChange]
  );

  const handleSortByChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onFilterChange({ sortBy: e.target.value });
    },
    [onFilterChange]
  );

  const handleSortOrderChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onFilterChange({ sortOrder: e.target.value as "asc" | "desc" });
    },
    [onFilterChange]
  );

  const handleStartDateChange = useCallback(
    (date: dayjs.Dayjs | null) => {
      setStartDate(date);
      onFilterChange({
        startDate: date ? date.toISOString() : undefined,
      });
    },
    [onFilterChange]
  );

  const handleEndDateChange = useCallback(
    (date: dayjs.Dayjs | null) => {
      setEndDate(date);
      onFilterChange({
        endDate: date ? date.toISOString() : undefined,
      });
    },
    [onFilterChange]
  );

  const handleClearFilters = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    onFilterChange({
      platformId: undefined,
      startDate: undefined,
      endDate: undefined,
      sortBy: "date",
      sortOrder: "desc",
    });
  }, [onFilterChange]);

  return (
    <div className="flex flex-col space-y-4">
      <h2 className="text-display text-lg font-semibold text-apple-gray-900">Filters</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Platform Filter */}
        <div>
          <label className="block text-sm font-medium text-apple-gray-700 mb-1">
            Platform (Active Platforms)
          </label>
          <select
            className="glass-input w-full border border-apple-gray-300 rounded-3xl py-2 px-3 focus:outline-none focus:ring-2 focus:ring-apple-blue-500 focus:border-apple-blue-500 hover-lift"
            value={filters.platformId || ""}
            onChange={handlePlatformChange}
          >
            <option value="">Select Platform</option>
            {platforms.filter((p: any) => p.isActive).map((platform) => (
              <option key={platform._id} value={platform._id}>
                {capitalize(platform.name)}
              </option>
            ))}
          </select>
        </div>

        {/* Date Range Filters */}
        <div>
          <label className="block text-sm font-medium text-apple-gray-700 mb-1">
            Start Date
          </label>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={startDate}
              onChange={handleStartDateChange}
              maxDate={endDate || undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  placeholder: "Select start date",
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.375rem",
                    },
                  },
                },
              }}
              format="MM/DD/YYYY"
            />
          </LocalizationProvider>
        </div>

        <div>
          <label className="block text-sm font-medium text-apple-gray-700 mb-1">
            End Date
          </label>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={endDate}
              onChange={handleEndDateChange}
              minDate={startDate || undefined}
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  placeholder: "Select end date",
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "0.375rem",
                    },
                  },
                },
              }}
              format="MM/DD/YYYY"
            />
          </LocalizationProvider>
        </div>

        {/* Sort Options */}
        <div>
          <label className="block text-sm font-medium text-apple-gray-700 mb-1">
            Sort By
          </label>
          <select
            className="glass-input w-full border border-apple-gray-300 rounded-3xl py-2 px-3 focus:outline-none focus:ring-2 focus:ring-apple-blue-500 focus:border-apple-blue-500 hover-lift"
            value={filters.sortBy}
            onChange={handleSortByChange}
          >
            <option value="date">Date</option>
            <option value="total_cost">Cost</option>
            <option value="createdAt">Created At</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-apple-gray-700 mb-1">
            Sort Order
          </label>
          <select
            className="glass-input w-full border border-apple-gray-300 rounded-3xl py-2 px-3 focus:outline-none focus:ring-2 focus:ring-apple-blue-500 focus:border-apple-blue-500 hover-lift"
            value={filters.sortOrder}
            onChange={handleSortOrderChange}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </select>
        </div>
      </div>

      {/* Clear Filters Button */}
      <div className="flex justify-end">
        <button
          onClick={handleClearFilters}
          className="px-4 py-2 text-sm text-apple-blue-600 hover:text-apple-blue-800 focus:outline-none hover-lift"
        >
          Clear Filters
        </button>
      </div>
    </div>
  );
};

export default UsageFilterBar;
